import type { Tables } from '../../types/database.types';

/**
 * CG Order Tracker (unification, Stage 2): order shapes and pure lenses.
 * Access is the database's job (the cg_members gate). Revenue and lead times
 * are derived here, never stored. Cost, profit and margin live in
 * orderProfit.ts, because since 20270162000100 they need the supplier's bill.
 */

/**
 * Which pipeline an order runs on.
 *
 * custom   — the bespoke path: suppliers, samples, a PO, a sign-off gate.
 * in_stock — warehouse stock: invoice, payment, fulfil, ship.
 *
 * The database column is plain text with a CHECK rather than an enum, so this
 * narrows it for the app. Everything that existed before 20261208000100 is
 * 'custom', including every stage.
 */
export type CgOrderType = 'custom' | 'in_stock';

export type CgOrder = Tables<'cg_orders'>;
export type CgOrderItem = Tables<'cg_order_items'>;
export type CgStatus = Tables<'cg_statuses'>;
export type CgProductType = Tables<'cg_product_types'>;
export type CgReportTemplate = Tables<'cg_report_templates'>;

export interface CgOrderWithItems extends CgOrder {
  items: CgOrderItem[];
}

export function itemLineRevenue(item: CgOrderItem): number {
  return item.units * Number(item.unit_price_usd);
}

/** Sum of line revenue plus shipping and handling. */
export function orderTotal(order: CgOrder, items: CgOrderItem[]): number {
  const revenue = items.reduce((sum, item) => sum + itemLineRevenue(item), 0);
  return revenue + Number(order.shipping_handling_usd);
}

export function orderRevenue(items: CgOrderItem[]): number {
  return items.reduce((sum, item) => sum + itemLineRevenue(item), 0);
}

function daysBetween(fromIso: string | null, toIso: string | null): number | null {
  if (fromIso === null || toIso === null) {
    return null;
  }
  const from = new Date(`${fromIso}T00:00:00Z`).getTime();
  const to = new Date(`${toIso}T00:00:00Z`).getTime();
  return Math.round((to - from) / 86_400_000);
}

/** Headline: PO sent to actual ship. */
export function daysToShip(order: CgOrder): number | null {
  return daysBetween(order.date_po_sent, order.date_shipped);
}

/**
 * Client responsiveness: invoice sent to invoice paid.
 *
 * Null until BOTH dates exist, which for every order that predates
 * 20261112000100 means null for good — nothing recorded when the invoice went
 * out. That is the honest answer, and the tempting alternative is exactly what
 * the column was added to stop: measuring from whatever date happened to be
 * nearby produces a number nobody can tell apart from a real one.
 */
export function daysToPay(order: CgOrder): number | null {
  return daysBetween(order.invoice_sent_on, order.date_paid);
}

/** Supplier responsiveness: PO sent to acknowledged. */
export function daysToAcknowledge(order: CgOrder): number | null {
  return daysBetween(order.date_po_sent, order.date_acknowledged);
}

/** Production speed: acknowledged to shipped. */
export function daysInProduction(order: CgOrder): number | null {
  return daysBetween(order.date_acknowledged, order.date_shipped);
}

/** Today as an ISO date, in the form a date input and a date column want. */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Days elapsed since PO was sent, for open orders against the ship target. */
export function daysSincePoSent(order: CgOrder, todayIso: string): number | null {
  if (order.date_shipped !== null) {
    return null;
  }
  return daysBetween(order.date_po_sent, todayIso);
}

/**
 * Out of sight, not merely finished.
 *
 * Distinct from isOpenStatus on purpose — see 20261117000200. Shipped and
 * Delivered are CLOSED to stall alerting and VISIBLE on the board; only
 * Closed is archived. Reading is_open for both is what made a delivered order
 * vanish from the Pipeline the moment somebody recorded its arrival.
 *
 * An unknown status is not archived: a row the vocabulary does not recognise
 * must show up somewhere, not be silently filed.
 */
export function isArchivedStatus(statusId: string, statuses: CgStatus[]): boolean {
  return statuses.find((status) => status.id === statusId)?.is_archived === true;
}

export function isOpenStatus(statusId: string, statuses: CgStatus[]): boolean {
  const found = statuses.find((status) => status.id === statusId);
  return found === undefined ? true : found.is_open;
}

/** Attention lens: open orders past the ship target since PO sent. */
export function attentionOrders(
  orders: CgOrder[],
  statuses: CgStatus[],
  targetDays: number,
  todayIso: string,
): { order: CgOrder; days: number }[] {
  const flagged: { order: CgOrder; days: number }[] = [];
  for (const order of orders) {
    if (!isOpenStatus(order.status, statuses)) {
      continue;
    }
    const days = daysSincePoSent(order, todayIso);
    if (days !== null && days > targetDays) {
      flagged.push({ order, days });
    }
  }
  return flagged.sort((a, b) => b.days - a.days);
}

/**
 * Orders opened automatically from an approved supplier sample, still waiting
 * for the details only the CG team knows. A blank client is the marker: the
 * bridge fills in everything it can and leaves the rest.
 */
export function draftOrders(orders: CgOrder[]): CgOrder[] {
  return orders.filter((order) => order.client.trim() === '');
}

export interface MonthlySalesPoint {
  month: string;
  label: string;
  total: number;
  orders: number;
}

/** Dashboard lens: totals by month of date_paid over the trailing N months. */
export function monthlySales(
  orders: CgOrder[],
  itemsByOrder: Map<string, CgOrderItem[]>,
  months: number,
  todayIso: string,
): MonthlySalesPoint[] {
  const points: MonthlySalesPoint[] = [];
  const today = new Date(`${todayIso}T00:00:00Z`);
  for (let back = months - 1; back >= 0; back -= 1) {
    const at = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - back, 1));
    const key = `${at.getUTCFullYear()}-${String(at.getUTCMonth() + 1).padStart(2, '0')}`;
    points.push({
      month: key,
      label: at.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' }),
      total: 0,
      orders: 0,
    });
  }
  const byMonth = new Map(points.map((point) => [point.month, point]));
  for (const order of orders) {
    if (order.date_paid === null) {
      continue;
    }
    const point = byMonth.get(order.date_paid.slice(0, 7));
    if (point === undefined) {
      continue;
    }
    point.total += orderTotal(order, itemsByOrder.get(order.id) ?? []);
    point.orders += 1;
  }
  return points;
}

export function searchOrders(orders: CgOrder[], query: string): CgOrder[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return orders;
  }
  return orders.filter((order) =>
    [order.order_no, order.client, order.region, order.status, order.notes]
      .join(' ')
      .toLowerCase()
      .includes(needle),
  );
}

/**
 * The one sentence somebody sees when the gate stops them.
 *
 * Exported because the banner shows it BEFORE the attempt as well as after —
 * telling somebody why the option is unavailable beats letting them press it
 * and be refused, and the two must say the same thing or they read as two
 * different problems.
 */
export const FINANCIAL_GATE_MESSAGE =
  'Cannot advance order: Deposit and payment verification tasks must be completed first.';

/** What a refused save says when the refusal is about access, not the data. */
export const CG_NO_PERMISSION_MESSAGE =
  "You don't have permission to make this change. Your access may have changed since the page loaded: reload it, and if you still need access, ask a TAP admin.";

/**
 * True for a row-security or table-grant refusal.
 *
 * The browser only ever gets error.message here (queries.ts fail(), and the
 * rpc wrappers in orderProjectLink.ts, throw new Error(error.message)), never
 * the SQLSTATE, so this matches the words Postgres uses, the same test
 * memberErrorMessage makes (projects/queries.ts). Deliberately NOT '42501' as
 * a substring: that would also fire on any message that merely contains
 * those digits. A refused FUNCTION is left out too: that is a deploy that
 * went out in halves, not a person without access, and the raw text is what
 * explains it (projectErrorMessage, projects/queries.ts).
 */
export function isAccessRefusal(message: string): boolean {
  const lowered = message.toLowerCase();
  if (lowered.includes('row-level security')) {
    return true;
  }
  return (
    lowered.includes('permission denied') && !lowered.includes('permission denied for function')
  );
}

export function cgErrorMessage(message: string): string {
  // The database's own refusal (20260927000100). The dropdown greys these
  // options out, so seeing this means the write came from somewhere the
  // interface was not — a stale tab, or a direct API call.
  if (message.includes('status_cannot_move_backwards')) {
    return 'This order is already in production or confirmed by the supplier, so its status cannot move backwards. Ask a TAP admin if it genuinely needs correcting.';
  }
  if (message.includes('cg_orders_order_no_key')) {
    return 'An order with this Order ID already exists.';
  }
  if (message.includes('cg_activity is append-only')) {
    return 'Activity records cannot be changed.';
  }
  // The financial gate (Batch 9d). The exact wording matters: somebody who
  // sees this needs to know WHICH work is outstanding, not just that they were
  // refused.
  if (message.includes('financial_gate_incomplete')) {
    return FINANCIAL_GATE_MESSAGE;
  }
  if (message.includes('financial_gate_override_reason_required')) {
    return 'Give a reason for the override. It goes into the order\u2019s audit trail.';
  }
  if (message.includes('financial_gate_override_not_permitted')) {
    return 'Only a TAP admin can override the financial gate.';
  }
  // signoff_certificates.cg_order_id is ON DELETE RESTRICT (20260929000100).
  // A certificate is the record of what a client approved and must outlive any
  // tidying up of the order it belongs to, so the database refuses the delete.
  // Without this the refusal arrives as a raw constraint name and the order
  // simply stays on screen, which reads as the button being broken.
  // The recorded-delete path (20261014000100).
  if (message.includes('signed_order_delete_reason_required')) {
    return 'This order was signed off by a client, so deleting it needs a reason. It is written to the activity log with your name.';
  }
  if (message.includes('signed_order_delete_not_permitted')) {
    return 'Only a TAP admin can delete an order a client has signed off.';
  }
  if (message.includes('signoff_certificates is append-only')) {
    return 'A sign-off certificate cannot be edited. An order can be deleted with a reason, but what a client approved is never rewritten.';
  }
  // The revision loop (20261004000100 + the drawer panel). These reached the
  // page as raw function names before the panel existed, because nothing in
  // the app had ever called upload_order_asset.
  if (message.includes('component_already_approved')) {
    return 'The client has already approved this component, so it cannot be replaced. What a client approved stays as it was.';
  }
  if (message.includes('asset_is_locked')) {
    return 'This proof is locked because the client approved it. Only components sent back for changes can be replaced.';
  }
  if (message.includes('not_authorized_for_cg_orders')) {
    return 'You need to be on the CG team to upload proofs for this order.';
  }
  if (message.includes('drive_reconnect_required')) {
    return 'Google Drive needs reconnecting — sign out and back in, then try the upload again.';
  }
  // Acknowledging a supplier's revision request (20261020000100).
  if (message.includes('no_revision_request')) {
    return 'There is nothing outstanding to mark handled — somebody may have answered this already. Refresh to see the current state.';
  }
  if (message.includes('not_a_cg_member')) {
    return 'You need to be on the CG team to answer a supplier on this order.';
  }
  // No supplier_handoffs row points at this order (20261022000100). Raised by
  // the RPC and, for the status dropdown, by cg_orders_guard_supplier_linked.
  // Without this the refusal arrived as a raw function name on a screen where
  // nothing had ever mentioned suppliers.
  // The client approval gate (20261024000100). Deliberately has no override,
  // so the message must not send anybody looking for one — the remedy is the
  // review link, which the drawer can now issue in one click.
  if (message.includes('client_approval_required')) {
    return 'The client has not signed this order off, so it cannot leave Order Placed. Issue a review link from the order and send it to them. There is no override for this one \u2014 client approval is required on every order.';
  }
  if (message.includes('order_has_no_supplier')) {
    return 'No supplier is linked to this order, so there is nobody to send the PO to. Link it to a supplier handoff first — until then the order would sit in “PO Sent to ES” while appearing in no supplier’s portal.';
  }
  // The order-number gate (20270109000100). The Shopify order does not exist
  // until the client approves the design, so a new order carries a placeholder
  // until somebody sets the real number. This is the last moment that is still
  // fixable: once the PO is out, the factory holds a sheet with the number
  // printed on it. The message names the button rather than the rule.
  if (message.includes('order_no_not_confirmed')) {
    return 'The Shopify order number on this order has never been confirmed. Open the order, press “Set Shopify order #” and put in the number Shopify gave it — or save the one already there if it is right. The PO cannot go out first, because the number is printed on it and cannot be changed afterwards.';
  }

  // Reaching this now means something took the old plain-delete path rather
  // than delete_cg_order. Kept as a safety net, but the advice is the current
  // one: deleting is possible, with a reason. Telling somebody to close the
  // order instead would be worse than useless — a closed order still counts in
  // revenue and margin reports, so it corrupts the numbers rather than tidying
  // them.
  if (message.includes('signoff_certificates_cg_order_id_fkey')) {
    return 'This order has a client sign-off certificate. A TAP admin can delete it by giving a reason, which is recorded in the activity log.';
  }
  // A row-security or table-grant refusal reaching here means access changed
  // after the page loaded (a stale tab). See isAccessRefusal.
  if (isAccessRefusal(message)) {
    return CG_NO_PERMISSION_MESSAGE;
  }
  return message;
}
